import { csrfHeaders } from './client.js';
import { ASSISTANT_NAME } from '../brand.js';

const PREFIX = 'jonathan.ownerTurn.v1:';
const memory = new Map();
const identifier = /^[A-Za-z0-9_.:-]{1,128}$/;

export class OwnerTurnError extends Error {
  constructor(message, { terminal = false, status = 0 } = {}) {
    super(message);
    this.terminal = terminal;
    this.status = status;
  }
}

function key(scope) {
  if (!/^[a-f0-9]{64}$/.test(scope || '')) throw new OwnerTurnError('Reload your salon dashboard before sending a message.');
  return PREFIX + scope;
}

function save(scope, record) {
  const storageKey = key(scope);
  memory.set(storageKey, record);
  try { sessionStorage.setItem(storageKey, JSON.stringify(record)); } catch { /* Memory remains usable when storage is blocked. */ }
  return record;
}

export function pendingOwnerTurn(scope) {
  const storageKey = key(scope);
  let record = memory.get(storageKey);
  if (!record) {
    try { record = JSON.parse(sessionStorage.getItem(storageKey) || 'null'); } catch { return null; }
  }
  if (!record) return null;
  if (record.scope !== scope || !identifier.test(record.turn_id || '') || !identifier.test(record.session_id || '') ||
      (record.request && (record.request.turn_id !== record.turn_id || !identifier.test(record.request.session_id || '')))) {
    throw new OwnerTurnError('The saved conversation request could not be restored. Please contact support.');
  }
  return record;
}

export function startOwnerTurn(scope, payload) {
  if (pendingOwnerTurn(scope)) throw new OwnerTurnError('Please finish recovering your previous reply first.');
  const request = JSON.parse(JSON.stringify({ ...payload,
    session_id: payload.session_id || crypto.randomUUID(), turn_id: crypto.randomUUID() }));
  return save(scope, { scope, turn_id: request.turn_id, session_id: request.session_id, request });
}

export function clearOwnerTurn(scope, turnId) {
  if (pendingOwnerTurn(scope)?.turn_id !== turnId) return;
  memory.delete(key(scope));
  try { sessionStorage.removeItem(key(scope)); } catch { /* Storage may be unavailable. */ }
}

export function clearOwnerTurnStorage() {
  memory.clear();
  try {
    for (const storageKey of Object.keys(sessionStorage)) {
      if (storageKey.startsWith(PREFIX)) sessionStorage.removeItem(storageKey);
    }
  } catch { /* Logout must remain available. */ }
}

async function request(path, options, signal, scope) {
  signal?.throwIfAborted();
  const controller = new AbortController();
  const abort = () => controller.abort(signal.reason);
  signal?.addEventListener('abort', abort, { once: true });
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(path, { credentials: 'include', ...options, headers: { ...options.headers, 'X-Owner-Chat-Scope': scope }, signal: controller.signal });
    let body;
    try { body = await response.json(); } catch (error) {
      if (response.ok) throw error;
      body = {};
    }
    signal?.throwIfAborted();
    return { response, body };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
}

function delay(signal) {
  return new Promise((resolve, reject) => {
    signal?.throwIfAborted();
    const abort = () => { clearTimeout(timer); reject(signal.reason); };
    const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, 1500);
    signal?.addEventListener('abort', abort, { once: true });
  });
}

export async function recoverOwnerTurn(scope, { signal, onAccepted } = {}) {
  let record = pendingOwnerTurn(scope);
  if (!record) throw new OwnerTurnError('There is no pending reply to recover.');
  while (true) {
    let { response, body } = await request(`/v1/owner/chat?turn_id=${encodeURIComponent(record.turn_id)}`, {}, signal, scope);
    if (response.status === 404 && record.request) {
      // A missing accepted record permits the exact immutable request to be sent again.
      ({ response, body } = await request('/v1/owner/chat', { method: 'POST',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify(record.request) }, signal, scope));
    }
    if (!response.ok) {
      const message = response.status === 401 ? 'Please sign in again to recover your reply.' :
        response.status === 403 ? 'Your account cannot access this conversation right now.' :
        response.status === 409 ? 'This conversation has another pending reply. Please reload the dashboard.' :
        'Your reply could not be checked. Please try recovering it again.';
      throw new OwnerTurnError(message, { status: response.status });
    }
    if (body.turn_id !== record.turn_id || !identifier.test(body.session_id || '') ||
        (!record.request && body.session_id !== record.session_id) ||
        !['pending', 'running', 'completed', 'failed'].includes(body.status)) {
      throw new OwnerTurnError('The saved reply could not be verified. Please try recovering it again.');
    }
    signal?.throwIfAborted();
    if (pendingOwnerTurn(scope)?.turn_id !== record.turn_id) throw new OwnerTurnError('The active conversation changed.');
    // Acceptance is durable: keep IDs only, removing the teaching text from browser storage.
    record = save(scope, { scope, turn_id: record.turn_id, session_id: body.session_id });
    onAccepted?.(record);
    if (body.status === 'failed') throw new OwnerTurnError(
      `${ASSISTANT_NAME} could not finish this reply. Some information may have been saved; please review it before trying again.`, { terminal: true });
    if (body.status === 'completed') {
      if (!body.result || body.result.session_id !== body.session_id || typeof body.result.response !== 'string') {
        throw new OwnerTurnError('The completed reply could not be verified. Please contact support.');
      }
      return { ...record, result: body.result };
    }
    await delay(signal);
  }
}
